/**
 * Navigation Component
 * 
 * Main navigation sidebar with expandable/collapsible functionality.
 * Expands on hover to show full menu with hierarchical structure including
 * all admin options and main modules.
 * 
 * Features:
 * - Collapsible sidebar (collapsed: icon only, expanded: 250px with labels)
 * - Hover-based expansion
 * - Hierarchical menu structure with parent/child items
 * - Dark theme support
 * - Keyboard shortcuts support
 * 
 * @module Components/Nav
 */

import { Icon } from '@shopify/polaris'
import {
    ChannelsIcon,
    EmailIcon,
    InventoryIcon,
    ChartHistogramGrowthIcon,
    DnsSettingsIcon,
    ListBulletedIcon,
    ProductIcon,
    GlobeIcon,
    ChartCohortIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    CashDollarIcon,
    DeliveryIcon,
    // LockIcon,
    SettingsIcon,
    // ClockIcon,
    // ChatIcon,
    PersonIcon,
    ReceiptDollarIcon,
} from '@shopify/polaris-icons'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { checkHasAccessModule } from '../../helpers/checkHasAccess'
// import DraggableAIAssistantFAB from '../AI/DraggableAIAssistantFAB'
import KeyboardShortcutsModal from '../../pages/DateRegion/KeyboardShortcutsModal'
import { useHotkeys } from 'react-hotkeys-hook'
import { resetUserData } from '../../core/redux/store/userSlice/userSlice'
import { auth } from '../../services/firebase/firebaseConfig'
import { signOut } from 'firebase/auth'
import UserInfoModal from '../../pages/DateRegion/UserInfoModal'

interface NavItem {
    path: string
    label: string
    icon: any
    tooltip?: string
    className?: string
    children?: NavItem[]
    showBadge?: boolean
    disabled?: boolean
}

const Nav = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const dispatch = useDispatch()
    const { isDarkTheme, email, firstName } = useSelector((state: any) => state.user)
    const [showingKeyboardShortcuts, showKeyboardShortcuts] = useState<boolean>(false)
    const [isExpanded, setIsExpanded] = useState<boolean>(false)
    const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({})
    const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState<boolean>(false)
    const [isUserModalOpen, setIsUserModalOpen] = useState<boolean>(false)

    const goToPage = useCallback(
        (path) => {
            if (window.location.pathname === path) {
                window.location.reload()
            } else {
                history.push(path)
            }
        },
        [history]
    )

    useHotkeys('shift + /', () => showKeyboardShortcuts((prev) => !prev))

    /**
     * Handle logout
     */
    const logout = useCallback(async () => {
        try {
            await signOut(auth)
            dispatch(resetUserData())
            window.location.assign('/login')
        } catch (error) {
            console.error('Error signing out:', error)
        }
    }, [dispatch])

    /**
     * Handle account details click
     */
    const handleAccountDetails = useCallback(() => {
        setIsAccountDropdownOpen(false)
        setIsUserModalOpen(true)
    }, [])

    /**
     * Collapsing the rail closes every submenu.
     *
     * There is deliberately no auto-expand counterpart. The admin menu used to be forced open
     * here on every expand, which made Settings read as the selected section on every page: its
     * ten children sat open on screen while every other group stayed shut, so the eye took the
     * whole block as active. Nothing highlighted the row — `.selected` comes only from
     * `isPathSelected`, which was correct throughout — the always-open submenu was the tell.
     *
     * A submenu now opens only when the user opens it, which is also what made the
     * `manuallyToggledMenus` bookkeeping unnecessary: it existed solely to stop this effect
     * reopening a menu somebody had just closed.
     */
    React.useEffect(() => {
        if (!isExpanded) setExpandedMenus({})
    }, [isExpanded])

    /**
     * Toggle submenu expansion
     */
    const toggleSubmenu = useCallback((path: string, e: React.MouseEvent) => {
        e.stopPropagation()
        setExpandedMenus(prev => ({
            ...prev,
            [path]: !prev[path]
        }))
    }, [])

    /**
     * Main navigation structure with hierarchical menus
     */
    const MainNavigations: NavItem[] = [
        { 
            path: '/dashboard', 
            label: 'Dashboard', 
            icon: GlobeIcon, 
            tooltip: 'Dashboard',
            className: 'guide-dashboard'
        },
        // Temporarily hidden - Records tab
        // { 
        //     path: '/dateRegion', 
        //     label: 'Records', 
        //     icon: ListBulletedIcon, 
        //     tooltip: 'Records',
        //     className: 'guide-dateRegion'
        // },
        {
            path: '/shipments',
            label: 'Shipments',
            icon: InventoryIcon,
            tooltip: 'All Shipments',
            className: 'guide-shipments'
        },
        {
            path: '/email-intake',
            label: 'Email Intake',
            icon: EmailIcon,
            tooltip: 'Orders arriving by email — review the parsed draft before it becomes an order',
        },
        {
            path: '/dispatch',
            label: 'Dispatch',
            icon: DeliveryIcon,
            tooltip: 'Dispatch board — kanban + map + route optimizer',
        },
        // {
        //     path: '/assets',
        //     label: 'SKUs',
        //     icon: ProductIcon,
        //     tooltip: 'SKUs',
        //     className: 'guide-assets',
        //     disabled: true
        // },
        {
            path: '/disputes',
            label: 'Invoices',
            icon: ReceiptDollarIcon,
            tooltip: 'Generated invoices, customer reviews and disputes',
        },
        {
            path: '/mapFleet',
            label: 'Network & Routing',
            icon: ChannelsIcon,
            className: 'guide-network',
            tooltip: 'Network & Routing',
            children: [
                {
                    path: '/mapFleet/network',
                    label: 'Network',
                    icon: ChannelsIcon,
                },
                {
                    path: '/mapFleet/routing',
                    label: 'Routing',
                    icon: DeliveryIcon,
                }
            ]
        },
        {
            path: '/analytics',
            label: 'Analytics',
            icon: ChartHistogramGrowthIcon,
            tooltip: 'Reports & Analytics',
            className: 'guide-analytics'
        },
        {
            path: '/kpis',
            label: 'KPIs',
            icon: ChartCohortIcon,
            tooltip: 'Key Performance Indicators',
            className: 'guide-kpis'
        },
        { 
            path: '/admin', 
            label: 'Settings', 
            icon: DnsSettingsIcon, 
            tooltip: 'Settings',
            className: 'guide-admin',
            children: [
                {
                    path: '/admin',
                    label: 'Admin Dashboard',
                    icon: DnsSettingsIcon,
                    className: 'admin-dashboard-main',
                },
                {
                    path: '/admin/core-business',
                    label: 'Core Business',
                    icon: SettingsIcon,
                    children: [
                        { path: '/admin/role', label: 'Roles', icon: null as any },
                        { path: '/admin/userRole', label: 'User Management', icon: null as any },
                        { path: '/admin/transport-partners/drivers', label: 'Drivers', icon: null as any },
                        { path: '/admin/vendor', label: 'Client Profiles', icon: null as any },
                        { path: '/admin/billing/substatus', label: 'Statuses', icon: null as any },
                        { path: '/admin/calendarSettings', label: 'Calendar Settings', icon: null as any },
                        { path: '/admin/notifications', label: 'Notifications', icon: null as any },
                        { path: '/admin/automations', label: 'Triggers', icon: null as any },
                        { path: '/admin/billing/services', label: 'Services', icon: null as any },
                        { path: '/admin/billing/conditions', label: 'Conditions', icon: null as any },
                        { path: '/admin/billing/globalUnits', label: 'Global Units', icon: null as any },
                        { path: '/admin/fields', label: 'Field Configuration', icon: null as any },
                        { path: '/admin/addressBook', label: 'Address Book', icon: null as any },
                    ]
                },
                {
                    path: '/admin/transport-partners',
                    label: 'Transport Partners',
                    icon: DeliveryIcon,
                    children: [
                        { path: '/admin/transport-partners/directory', label: 'Partner Directory', icon: null as any },
                        { path: '/admin/transport-partners/profiles', label: 'Partner Profiles', icon: null as any },
                        // { path: '/admin/midmile/settings', label: 'Partner Settings', icon: null as any, disabled: true },
                    ]
                },
                // {
                //     path: '/admin/operations',
                //     label: 'Operations & Routing',
                //     icon: ClockIcon,
                //     disabled: true,
                //     children: [
                //         { path: '/admin/operations/re-optimization', label: 'Real-Time Re-Routing', icon: null as any, disabled: true },
                //         { path: '/admin/operations/capacity-management', label: 'Capacity Management', icon: null as any, disabled: true },
                //     ]
                // },
                {
                    path: '/admin/billing',
                    label: 'Billing & Settlement',
                    icon: CashDollarIcon,
                    children: [
                        { path: '/admin/rates', label: 'Rates Management', icon: null as any },
                        // { path: '/admin/billing/audit', label: 'Audit & Adjustments', icon: null as any, disabled: true },
                    ]
                },
                // {
                //     path: '/admin/ai',
                //     label: 'ML & AI',
                //     icon: ChatIcon,
                //     disabled: true,
                //     children: [
                //         { path: '/admin/ai/shyftbase', label: 'Shyftbase AI', icon: null as any, disabled: true },
                //         { path: '/admin/ai/optimizer', label: 'Machine Learning', icon: null as any, disabled: true },
                //         { path: '/admin/ai/agent-configuration', label: 'AI Agent Configuration', icon: null as any, disabled: true },
                //     ]
                // },
                {
                    path: '/admin/integrations',
                    label: 'Integrations',
                    icon: ProductIcon,
                    children: [
                        { path: '/admin/integrations/external', label: '3rd Party Integrations', icon: null as any },
                        // { path: '/admin/integrations/internal', label: 'Shyftbase Connect', icon: null as any, disabled: true },
                    ]
                },
                // {
                //     path: '/admin/compliance',
                //     label: 'Security & Compliance',
                //     icon: LockIcon,
                //     disabled: true,
                //     children: [
                //         { path: '/admin/compliance/gdpr', label: 'GDPR Management', icon: null as any, disabled: true },
                //         { path: '/admin/compliance/country-rules', label: 'Country-Specific Rules', icon: null as any, disabled: true },
                //         { path: '/admin/compliance/audit-trails', label: 'Audit & Compliance', icon: null as any, disabled: true },
                //     ]
                // },
            ]
        }
    ]

    /**
     * Check if a path is currently selected
     */
    const isPathSelected = useCallback((path: string): boolean => {
        const currentPath = window.location.pathname
        if (path === '/dashboard' && currentPath === '/') return true
        if (path === '/dateRegion' && currentPath.includes('records')) return true
        return currentPath.includes(path)
    }, [])

    /**
     * Render submenu items
     */
    const renderSubmenu = useCallback((items: NavItem[], parentPath: string, level = 1) => {
        if (!items || !isExpanded) return null

        return (
            <div className={`nav-submenu level-${level}`}>
                {items.map((item, index) => {
                    const hasChildren = item.children && item.children.length > 0
                    const isSubmenuExpanded = expandedMenus[item.path]
                    const isSelected = isPathSelected(item.path)
                    const isDisabled = item.disabled === true

                    return (
                        <div key={`submenu-${item.path}-${index}`} className="nav-submenu-item-wrapper">
                            <div
                                className={`nav-submenu-item ${isSelected ? 'selected' : ''} ${
                                    isDarkTheme ? 'dark_theme' : ''
                                } ${isDisabled ? 'disabled' : ''} ${item.className || ''}`}
                                onClick={(e) => {
                                    if (isDisabled) {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        return
                                    }
                                    if (hasChildren) {
                                        toggleSubmenu(item.path, e)
                                    } else {
                                        goToPage(item.path)
                                    }
                                }}
                                role="button"
                                tabIndex={isDisabled ? -1 : 0}
                                aria-disabled={isDisabled}
                            >
                                {item.icon && (
                                    <div className="nav-submenu-icon">
                                        <Icon source={item.icon as any} />
                                    </div>
                                )}
                                <span className="nav-submenu-label">{item.label}</span>
                                {hasChildren && (
                                    <div className="nav-submenu-chevron">
                                        <Icon source={isSubmenuExpanded ? ChevronDownIcon as any : ChevronRightIcon as any} />
                                    </div>
                                )}
                            </div>
                            {hasChildren && isSubmenuExpanded && renderSubmenu(item.children!, item.path, level + 1)}
                        </div>
                    )
                })}
            </div>
        )
    }, [isExpanded, expandedMenus, isDarkTheme, isPathSelected, toggleSubmenu, goToPage])

    /**
     * Render main navigation item
     */
    const renderNavItem = useCallback(
        (item: NavItem, index: number) => {
            const isSelected = isPathSelected(item.path)
            const hasChildren = item.children && item.children.length > 0
            const isSubmenuExpanded = expandedMenus[item.path]
            const isDisabled = item.disabled === true

            return (
                <div key={`nav_${item.path}_${index}`} className="nav-item-wrapper">
                <div
                    className={`shyftbase_main_nav_item nav-item-animate ${isSelected ? 'selected' : ''} ${
                        isDarkTheme ? 'dark_theme' : ''
                    } ${isDisabled ? 'disabled' : ''} ${item.className || ''}`}
                        onClick={(e) => {
                            if (isDisabled) {
                                e.preventDefault()
                                e.stopPropagation()
                                return
                            }
                            if (hasChildren && isExpanded) {
                                toggleSubmenu(item.path, e)
                            } else {
                                goToPage(item.path)
                            }
                        }}
                    onKeyDown={(e) => {
                        if (isDisabled) {
                            e.preventDefault()
                            return
                        }
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                                if (hasChildren && isExpanded) {
                                    toggleSubmenu(item.path, e as any)
                                } else {
                                    goToPage(item.path)
                                }
                            }
                        }}
                        role="button"
                    tabIndex={isDisabled ? -1 : 0}
                        aria-label={item.label}
                        aria-disabled={isDisabled}
                >
                        {!isExpanded && <span className="sb_tooltiptext_nav">{item.tooltip || item.label}</span>}
                    <div className="nav-icon-container">
                            <Icon source={item.icon as any} />
                        {item.showBadge && <span className="notification-badge"></span>}
                    </div>
                        {isExpanded && (
                            <>
                                <span className="nav-label">{item.label}</span>
                                {hasChildren && (
                                    <div className="nav-chevron">
                                        <Icon source={isSubmenuExpanded ? ChevronDownIcon as any : ChevronRightIcon as any} />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {hasChildren && isSubmenuExpanded && renderSubmenu(item.children!, item.path)}
                </div>
            )
        },
        [goToPage, isExpanded, isDarkTheme, isPathSelected, expandedMenus, toggleSubmenu, renderSubmenu]
    )

    return (
        <nav
            className={`shyftbase_nav ${isExpanded ? 'expanded' : 'collapsed'} ${isDarkTheme ? 'dark_theme' : ''}`}
            role="navigation"
            aria-label="main navigation"
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => {
                setIsExpanded(false)
                setExpandedMenus({})
                setIsAccountDropdownOpen(false)
            }}
        >
            <div className="shyftbase_main_nav">
                <div
                    className={`shyftbase_main_nav_item logo-item ${
                        isDarkTheme ? 'logo_dark_theme' : 'logo_light_theme'
                    }`}
                >
                    <img
                        src={
                            isDarkTheme
                                ? 'https://firebasestorage.googleapis.com/v0/b/public-contents.appspot.com/o/SHYFTBASE%2Fshyftbase_logo_light.png?alt=media&token=1db72423-63b9-4b0e-baa2-4778eea21e38'
                                : 'https://firebasestorage.googleapis.com/v0/b/public-contents.appspot.com/o/SHYFTBASE%2Fshyftbase_logo.png?alt=media&token=75278299-a954-43fd-b1f0-71904e969c56'
                        }
                        alt="Shyftbase Logo"
                        width={35}
                    />
                    {isExpanded && <span className="logo-text">Shyftbase</span>}
                </div>
                <div className="shyftbase_main_nav_items">
                    {MainNavigations.filter((item) => checkHasAccessModule(item.path)).map(renderNavItem)}
                </div>
                
                {/* Account Menu - Always visible at bottom */}
                <div className="nav-account-menu" style={{ 
                    borderTop: isDarkTheme ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                    paddingTop: '0.5rem',
                    marginTop: 'auto'
                }}>
                    <div
                        className={`shyftbase_main_nav_item nav-item-animate ${isDarkTheme ? 'dark_theme' : ''}`}
                        onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setIsAccountDropdownOpen(!isAccountDropdownOpen)
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label="Account menu"
                    >
                        {!isExpanded && <span className="sb_tooltiptext_nav">Account</span>}
                        <div className="nav-icon-container">
                            <Icon source={PersonIcon as any} />
                        </div>
                        {isExpanded && (
                            <>
                                <span className="nav-label">Account</span>
                                <div className="nav-chevron">
                                    <Icon source={isAccountDropdownOpen ? ChevronDownIcon as any : ChevronRightIcon as any} />
                                </div>
                            </>
                        )}
                    </div>
                    
                    {/* Account Dropdown Menu */}
                    {isExpanded && isAccountDropdownOpen && (
                        <div className="nav-account-dropdown" style={{
                            background: isDarkTheme ? 'rgba(45, 45, 45, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                            borderRadius: '8px',
                            margin: '0.5rem 0.5rem 0.5rem 0.75rem',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                            border: isDarkTheme ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden'
                        }}>
                            {/* User Info Header */}
                            <div style={{ 
                                padding: '12px 16px',
                                borderBottom: isDarkTheme ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                                color: isDarkTheme ? '#ffffff' : '#000000'
                            }}>
                                <p style={{ fontWeight: 600, marginBottom: '4px', fontSize: '13px' }}>
                                    {firstName ? `${firstName}` : 'Shyftbase Inc'}
                                </p>
                                {email && (
                                    <p style={{ 
                                        fontSize: '0.75rem',
                                        color: isDarkTheme ? '#aaaaaa' : '#666666'
                                    }}>{email}</p>
                                )}
                            </div>
                            
                            {/* Menu Items */}
                            <div style={{ padding: '4px 0' }}>
                                <div
                                    className={`nav-submenu-item ${isDarkTheme ? 'dark_theme' : ''}`}
                                    onClick={handleAccountDetails}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            handleAccountDetails()
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="nav-submenu-label">Account Details</span>
                                </div>
                                
                                <a
                                    href="https://www.shyftbase.com/demo"
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`nav-submenu-item ${isDarkTheme ? 'dark_theme' : ''}`}
                                    style={{ 
                                        textDecoration: 'none',
                                        display: 'block',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span className="nav-submenu-label">Contact sales</span>
                                </a>
                                
                                <a
                                    href="https://www.shyftbase.com/resources/integrations"
                                    target="_blank"
                                    rel="noreferrer"
                                    className={`nav-submenu-item ${isDarkTheme ? 'dark_theme' : ''}`}
                                    style={{ 
                                        textDecoration: 'none',
                                        display: 'block',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <span className="nav-submenu-label">Integrations</span>
                                </a>
                                
                                <div style={{ 
                                    height: '1px',
                                    background: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                                    margin: '4px 0'
                                }} />
                                
                                <div
                                    className={`nav-submenu-item ${isDarkTheme ? 'dark_theme' : ''}`}
                                    onClick={logout}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault()
                                            logout()
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <span className="nav-submenu-label">Log out</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Temporarily hidden - AI Assistant */}
                {/* <DraggableAIAssistantFAB /> */}
                <KeyboardShortcutsModal
                    isOpen={showingKeyboardShortcuts}
                    onClose={() => showKeyboardShortcuts(false)}
                />
            </div>
            <UserInfoModal 
                isOpen={isUserModalOpen} 
                onClose={() => setIsUserModalOpen(false)} 
            />
        </nav>
    )
}

export default React.memo(Nav)
